import React from 'react';
import { createRoot } from 'react-dom/client';

import { Provider } from 'react-redux';

import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';

import * as Sentry from '@sentry/react';

import './index.css';

import App from './components/App';
import { reloadOnce } from './components/utils/lazyWithRetry';
import { isStuckStoreError } from './utils/stuckReduxStore';

import reducers from './reducers';

import { apiSlice } from './slices/apiSlice';
import { activityFeedApi } from './slices/activityFeedApi';
import { remindersApi } from './slices/remindersApi';
import { apiTokensApi } from './slices/apiTokensApi';
import { webhookEndpointApi } from './slices/webhookEndpointApi';

const store = configureStore({
	reducer: reducers,
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware().concat(
			apiSlice.middleware,
			activityFeedApi.middleware,
			remindersApi.middleware,
			apiTokensApi.middleware,
			webhookEndpointApi.middleware
		),
	devTools: true,
});

// Tracks window focus/online state for RTK Query so options like
// skipPollingIfUnfocused (used by ReminderAlerts) actually take effect.
setupListeners(store.dispatch);

// A stuck store throws from plain event handlers (focus, sockets) as well as
// renders, and those never reach the ErrorBoundary. Reload once so the page
// gets a working store again (see utils/stuckReduxStore).
const reloadIfStoreStuck = (error: unknown) => {
	if (isStuckStoreError(error)) {
		reloadOnce();
	}
};
window.addEventListener('error', (event) => reloadIfStoreStuck(event.error));
window.addEventListener('unhandledrejection', (event) =>
	reloadIfStoreStuck(event.reason)
);

const container = document.querySelector('#root');

const root = createRoot(container!, {
	// Callback called when an error is thrown and not caught by an ErrorBoundary.
	onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
		console.warn('Uncaught error', error, errorInfo.componentStack);
	}),
	// Callback called when React catches an error in an ErrorBoundary.
	onCaughtError: Sentry.reactErrorHandler(),
	// Callback called when React automatically recovers from errors.
	onRecoverableError: Sentry.reactErrorHandler(),
}); // createRoot(container!) if you use TypeScript

root.render(
	<Provider store={store}>
		<App />
	</Provider>
);
